/**
 * CellCogMarkdown — the single markdown renderer used across the CellCog UI.
 *
 * Why this component exists:
 *   The previous implementation used `markdown-to-jsx`, which is not
 *   CommonMark-compliant and incorrectly permitted intraword underscore
 *   emphasis (e.g. `file_test_name` rendered with italics). It also forced
 *   every call site to string-preprocess SHOW_FILE tags into `![alt](url)`
 *   markdown image syntax before parsing, which leaked those tags into code
 *   blocks, tables, and inline-code spans where they were meant to be
 *   literal text. And it had no dedicated affordance for the SDK's
 *   GENERATE_FILE tag, so that tag rendered as escaped HTML text.
 *
 *   This component fixes all three issues at the parser level by building
 *   on react-markdown 9 + remark-gfm + a small remark plugin that walks
 *   the mdast tree after parsing:
 *
 *     - Underscore emphasis: handled correctly by remark/micromark per the
 *       CommonMark spec. No preprocessing.
 *     - SHOW_FILE: converted to a custom `cellcog-show-file` hast element
 *       by the plugin, which then routes to the ShowFileRenderer
 *       component. The plugin walks text, inlineCode AND code nodes so
 *       tags render as files no matter where the agent put them. Surviving
 *       non-tag content inside the original code fragment stays as code
 *       with its original language preserved.
 *     - GENERATE_FILE: similar — converted to a `cellcog-generate-file`
 *       hast element, rendered as a clean chip in chat surfaces and
 *       hidden elsewhere.
 *     - TeX math (CEL-2102): `$$...$$` (display or inline) renders via
 *       remark-math + rehype-katex. Both plugins and the KaTeX stylesheet
 *       are LAZY-LOADED by useMathPlugins only for content that contains
 *       math (see plugins/mathDetect.js), so the entry bundle is
 *       unaffected. Single-dollar `$...$` is NEVER math (prices, Stripe
 *       URLs with `_` would otherwise flip the whole message into math).
 *
 * Surfaces that consume this component:
 *   - ChatMessageContent        enableCellCogTags="chat"
 *   - StreamingTextContent      enableCellCogTags="chat"
 *   - PostContent               enableCellCogTags="none"
 *   - MarkdownContent (viewer)  enableCellCogTags="none", richCodeDefaultMode="rich"
 *   - MarkdownPreview (in code) enableCellCogTags="none", codeBlockComponent={null}
 *   - SupportDocsPage           enableCellCogTags="none"
 *   - SkillDetailPage           enableCellCogTags="none"
 *
 * Each surface passes the right code-block component so Shiki-based
 * highlighting stays identical to today.
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SmartTableComponent from '../SmartTableComponent';
import { makeCodeComponent, PreFragment } from './components/CodeComponents';
import GenerateFileChip from './components/GenerateFileChip';
import LinkComponent from './components/LinkComponent';
import OrderedList from './components/OrderedList';
import ShowFileRenderer from './components/ShowFileRenderer';
import remarkCellCogTags from './plugins/remarkCellCogTags';
import remarkTrimAutolinkTrailingJunk from './plugins/remarkTrimAutolinkTrailingJunk';
import rehypeStripBlockWhitespace from './plugins/rehypeStripBlockWhitespace';
import useMathPlugins from './useMathPlugins';
import './CellCogMarkdown.css';

/**
 * CellCogMarkdown
 *
 * Props:
 *   content                  string              Markdown source.
 *   isDarkMode               bool                Theme flag (drives table + general styling).
 *   enableCellCogTags        "none" | "chat"     Whether to handle SHOW_FILE / GENERATE_FILE.
 *   blobNameToUrl            object              Required when enableCellCogTags === "chat".
 *   messageIndex             number              Required when enableCellCogTags === "chat".
 *   codeBlockComponent       Component | null    Renderer for fenced code blocks.
 *                                                 - RichCodeBlock for chat / posts / file viewer
 *                                                 - StreamingCodeBlock for streaming chat
 *                                                 - null for plain <pre><code> (MarkdownPreview)
 *   richCodeDefaultMode      "raw" | "rich"      Default mode passed to codeBlockComponent.
 *   pendingVariant           "none" | "spinner"  What to render when a SHOW_FILE tag's blob
 *                            | "missing"         isn't resolved:
 *                                                 - "spinner" (streaming chat): "Loading media..."
 *                                                 - "missing" (finalized chat): warning tile
 *                                                 - "none" (posts / file viewer / public docs):
 *                                                   render nothing. Default.
 *   extraComponents          object              Extra component overrides merged on top
 *                                                 of the built-in map. Used by SupportDocsPage
 *                                                 to supply its own internal-doc link renderer.
 *                                                 Keys match react-markdown component names
 *                                                 (e.g. `a`, `h1`, `img`, custom tag names).
 *   className                string              Extra class on the outer wrapper.
 */
const CellCogMarkdown = ({
  content,
  isDarkMode,
  enableCellCogTags = 'none',
  blobNameToUrl,
  messageIndex,
  mediaIdPrefix = 'msg', // channel surfaces pass `ch-<msgId>` / `thr-<msgId>` (see ShowFileRenderer)
  codeBlockComponent = null,
  richCodeDefaultMode = 'raw',
  hideCodeBlockToggle = false,
  pendingVariant = 'none',
  extraComponents,
  className = '',
}) => {
  // Note: mediaIndex for each SHOW_FILE is assigned at the AST level by
  // the remarkCellCogTags plugin, so it is stable across re-renders.
  // The ShowFile renderer reads it from props instead of computing it
  // here.

  // null for math-free content (the common case); plugin tuples once the
  // lazy KaTeX chunk has loaded for content that contains TeX.
  const math = useMathPlugins(content);

  const remarkPlugins = useMemo(() => {
    // remarkTrimAutolinkTrailingJunk runs on all surfaces: it only touches
    // bare-URL autolinks whose href ends in a backtick/quote (never
    // explicit [text](url) links), so it is safe everywhere.
    const plugins = [remarkGfm, remarkTrimAutolinkTrailingJunk];
    // remark-math produces `math` / `inlineMath` nodes, which
    // remarkCellCogTags never walks (it visits text/inlineCode/code), so
    // the two are independent regardless of order.
    if (math) plugins.push(math.remark);
    if (enableCellCogTags === 'chat') {
      plugins.push(remarkCellCogTags);
    }
    return plugins;
  }, [enableCellCogTags, math]);

  // rehypeStripBlockWhitespace removes the "\n" text nodes that
  // remark-rehype inserts between sibling block elements. Without it,
  // those anonymous inline boxes break vertical margin collapsing —
  // producing doubled-up gaps between every paragraph/heading/list.
  // This restores the pre-migration (markdown-to-jsx) block-flow
  // behavior exactly. Always enabled; safe for all surfaces.
  // rehype-katex (when present) runs first: it replaces the
  // <pre><code class="language-math"> nodes with rendered KaTeX before
  // the code/pre component overrides ever see them.
  const rehypePlugins = useMemo(
    () => (math ? [math.rehype, rehypeStripBlockWhitespace] : [rehypeStripBlockWhitespace]),
    [math],
  );

  const CodeDispatch = useMemo(
    () =>
      makeCodeComponent({
        CodeBlockComponent: codeBlockComponent,
        defaultMode: richCodeDefaultMode,
        hideToggle: hideCodeBlockToggle,
      }),
    [codeBlockComponent, richCodeDefaultMode, hideCodeBlockToggle],
  );

  const TableComponent = useMemo(
    () =>
      function Table({ children, ...props }) {
        return (
          <SmartTableComponent isDarkMode={isDarkMode} {...props}>
            {children}
          </SmartTableComponent>
        );
      },
    [isDarkMode],
  );

  const ShowFile = useMemo(
    () =>
      function ShowFile({ fileUri, mediaIndex, ...rest }) {
        return (
          <ShowFileRenderer
            fileUri={fileUri}
            blobNameToUrl={blobNameToUrl}
            messageIndex={messageIndex}
            mediaIdPrefix={mediaIdPrefix}
            mediaIndex={typeof mediaIndex === 'string' ? parseInt(mediaIndex, 10) : mediaIndex || 0}
            pendingVariant={pendingVariant}
            attributes={rest}
          />
        );
      },
    [blobNameToUrl, messageIndex, mediaIdPrefix, pendingVariant],
  );

  const GenerateFile = useMemo(
    () =>
      function GenerateFile(props) {
        if (enableCellCogTags !== 'chat') return null;
        return <GenerateFileChip {...props} />;
      },
    [enableCellCogTags],
  );

  // react-markdown 9.x consumes the `components` map keyed by element name.
  // Custom element names come from our remark plugin's `hName` values.
  // extraComponents (caller-supplied) are merged last so they can override
  // any built-in (e.g. SupportDocsPage replaces `a` with its internal-doc
  // link renderer).
  const components = useMemo(
    () => ({
      a: LinkComponent,
      code: CodeDispatch,
      pre: PreFragment,
      ol: OrderedList,
      table: TableComponent,
      'cellcog-show-file': ShowFile,
      'cellcog-generate-file': GenerateFile,
      ...(extraComponents || {}),
    }),
    [CodeDispatch, TableComponent, ShowFile, GenerateFile, extraComponents],
  );

  return (
    <div
      className={`cellcog-markdown message-content markdown-content ${
        isDarkMode ? 'dark-theme' : 'light-theme'
      } ${className}`.trim()}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
};

export default React.memo(CellCogMarkdown);
